import { forwardRef, useState, type FormEvent, type ChangeEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { faInstagram } from '@fortawesome/free-brands-svg-icons';
import {
  Box, Button, Card, Container, Flex, Heading, Select, Separator, Text, TextArea, TextField,
} from '@radix-ui/themes';
import config from '../../config';
import './WorkWithUs.css';
import SEO from '../SEO/SEO';
import { analytics } from '../../utils/analytics';

interface FormData {
  name: string;
  email: string;
  phone: string;
  eventType: string;
  message: string;
}

type SubmitStatus = 'success' | 'error' | null;

const EVENT_TYPE_OPTIONS = [
  { value: 'venue', label: 'Weekly Trivia at My Venue' },
  { value: 'corporate', label: 'Corporate Event / Team Building' },
  { value: 'private', label: 'Private Party / Birthday' },
  { value: 'fundraiser', label: 'Fundraiser' },
  { value: 'other', label: 'Other' },
];

const WorkWithUs = forwardRef<HTMLDivElement>((_, ref) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEventTypeChange = (value: string) => {
    setFormData((prev) => ({ ...prev, eventType: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${config.apiBaseUrl}/email/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          fromAddress: formData.email,
          emailContent: `Phone: ${formData.phone || 'Not provided'}
Interest: ${formData.eventType}
Message: ${formData.message || 'No additional details'}`,
          type: 'Booking Request',
        }),
      });

      if (!response.ok) throw new Error('Failed to send');

      setSubmitStatus('success');
      analytics.contactFormSubmitted(formData.eventType);
      setFormData({ name: '', email: '', phone: '', eventType: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box ref={ref} className="contact-page" p={{ initial: '5', md: '6' }}>
      <SEO
        title="Book West Slope Quiz | Private Events & Venue Partnerships"
        description="Book West Slope Quiz for your venue, corporate event, private party, or fundraiser in Grand Junction and the Grand Valley. Get in touch — we'll get back to you within 24 hours."
        canonical="/workwithus"
      />
      <Container size="3">
        <Heading as="h1" size={{ initial: '6', md: '7' }} align="center" mb="4">
          Contact Us
        </Heading>
        <Box maxWidth="600px" mx="auto" mb="5">
          <Text as="p" align="center" color="gray">
            Ready to bring West Slope Quiz to your venue or event? Reach out directly or fill out the form and we'll get back to you within 24 hours.
          </Text>
        </Box>

        <Flex direction={{ initial: 'column', md: 'row' }} gap={{ initial: '5', md: '6' }} align="start">
          {/* Contact Info */}
          <Box width={{ initial: '100%', md: 'auto' }} className="contact-info-col">
            <Card size="3">
              <Heading as="h2" size="4" mb="3">Get in Touch</Heading>

              <Flex direction="column" gap="2">
                <Flex asChild align="center" gap="2" py="1">
                  <a href="tel:+19706844311" className="contact-item">
                    <FontAwesomeIcon icon={faPhone} className="contact-icon" />
                    <span>(970) 684-4311</span>
                  </a>
                </Flex>

                <Separator size="4" />

                <Flex asChild align="center" gap="2" py="1">
                  <a href="mailto:contact@westslopequiz.com" className="contact-item">
                    <FontAwesomeIcon icon={faEnvelope} className="contact-icon" />
                    <span>contact@westslopequiz.com</span>
                  </a>
                </Flex>

                <Separator size="4" />

                <Flex asChild align="center" gap="2" py="1">
                  <a
                    href="https://www.instagram.com/west_slope_quiz_co/"
                    target="_blank"
                    rel="noreferrer"
                    className="contact-item"
                  >
                    <FontAwesomeIcon icon={faInstagram} className="contact-icon" />
                    <span>@west_slope_quiz_co</span>
                  </a>
                </Flex>
              </Flex>
            </Card>
          </Box>

          {/* Booking Form */}
          <Box flexGrow="1" width="100%">
            <Card size="3">
              <form onSubmit={handleSubmit}>
                <Heading as="h2" size="4" mb="3">Book an Event</Heading>

                <Flex direction="column" gap="4">
                  <Flex direction="column" gap="1">
                    <Text as="label" size="2" weight="medium" htmlFor="name">Name *</Text>
                    <TextField.Root
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                    />
                  </Flex>

                  <Flex direction="column" gap="1">
                    <Text as="label" size="2" weight="medium" htmlFor="email">Email *</Text>
                    <TextField.Root
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                    />
                  </Flex>

                  <Flex direction="column" gap="1">
                    <Text as="label" size="2" weight="medium" htmlFor="phone">Phone</Text>
                    <TextField.Root
                      id="phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 555-5555"
                    />
                  </Flex>

                  <Flex direction="column" gap="1">
                    <Text as="label" size="2" weight="medium">What are you interested in? *</Text>
                    <Select.Root
                      value={formData.eventType}
                      onValueChange={handleEventTypeChange}
                      required
                      name="eventType"
                    >
                      <Select.Trigger
                        placeholder="Select an option..."
                        aria-label="What are you interested in?"
                      />
                      <Select.Content position="popper" sideOffset={4}>
                        {EVENT_TYPE_OPTIONS.map((option) => (
                          <Select.Item key={option.value} value={option.value}>
                            {option.label}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                  </Flex>

                  <Flex direction="column" gap="1">
                    <Text as="label" size="2" weight="medium" htmlFor="message">Tell us more</Text>
                    <TextArea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Date, location, number of guests, or any other details..."
                    />
                  </Flex>

                  <Button type="submit" size="3" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Request'}
                  </Button>

                  {submitStatus === 'success' && (
                    <Text as="p" align="center" color="green" weight="medium">
                      Thanks for reaching out! We'll be in touch soon.
                    </Text>
                  )}

                  {submitStatus === 'error' && (
                    <Text as="p" align="center" color="red" weight="medium">
                      Something went wrong. Please try again or email us directly.
                    </Text>
                  )}
                </Flex>
              </form>
            </Card>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
});

WorkWithUs.displayName = 'WorkWithUs';

export default WorkWithUs;
